import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supportAPI, getErrorMessage } from "../../services/api";
import AdminLayout from "../../components/admin/AdminLayout";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  open: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
};

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-gray-100 text-gray-600",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    loadTickets();
  }, [filterStatus]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 100 };
      if (filterStatus) params.status = filterStatus;
      const res = await supportAPI.adminTickets(params);
      setTickets(res.data);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await supportAPI.addMessage(selectedTicket.id, { message: reply.trim() });
      toast.success("Reply sent!");
      setReply("");
      // Refresh selected ticket
      const res = await supportAPI.getTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      loadTickets();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReplying(false);
    }
  };

  const handleUpdateStatus = async (ticketId: string, status: string) => {
    try {
      await supportAPI.adminUpdateTicket(ticketId, { status });
      toast.success("Ticket updated");
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        const res = await supportAPI.getTicket(ticketId);
        setSelectedTicket(res.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <AdminLayout>
      <Helmet><title>Support – Admin | Kushi Fashions</title></Helmet>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input-field py-1.5 text-sm w-40">
          <option value="">All Statuses</option>
          {["open", "in_progress", "resolved", "closed"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Ticket list */}
        <div className="space-y-3 max-h-[75vh] overflow-y-auto">
          {loading ? (
            <LoadingSpinner className="py-10" />
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No tickets found</div>
          ) : (
            tickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`w-full text-left card p-4 hover:shadow-md transition-shadow ${selectedTicket?.id === ticket.id ? "ring-2 ring-primary-400" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 line-clamp-1">{ticket.subject}</p>
                    <p className="text-xs text-gray-400 mt-0.5">#{ticket.ticket_number} · {ticket.category} · {ticket.messages?.length || 0} messages</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`badge ${STATUS_COLORS[ticket.status] || "bg-gray-100"}`}>{ticket.status.replace("_", " ")}</span>
                    <span className={`badge ${PRIORITY_COLORS[ticket.priority] || "bg-gray-100"}`}>{ticket.priority}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">{format(new Date(ticket.created_at), "dd MMM, h:mm a")}</p>
              </button>
            ))
          )}
        </div>

        {/* Ticket detail */}
        <div>
          {selectedTicket ? (
            <div className="card p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs text-gray-400">#{selectedTicket.ticket_number}</p>
                  <h2 className="font-bold text-gray-900">{selectedTicket.subject}</h2>
                </div>
                <select
                  value={selectedTicket.status}
                  onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                  className="input-field py-1 text-xs w-32"
                >
                  {["open", "in_progress", "resolved", "closed"].map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
              </div>

              {/* Messages */}
              <div className="space-y-3 max-h-64 overflow-y-auto mb-4 bg-gray-50 rounded-xl p-3">
                {selectedTicket.messages?.map((msg: any) => (
                  <div key={msg.id} className={`flex ${msg.is_admin ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                      msg.is_admin ? "bg-white text-gray-800 shadow-sm" : "bg-primary-100 text-primary-900"
                    }`}>
                      <p className="text-xs font-medium mb-0.5 opacity-60">{msg.sender_name}</p>
                      <p>{msg.message}</p>
                      <p className="text-xs opacity-40 mt-1">{format(new Date(msg.created_at), "h:mm a")}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply form */}
              {selectedTicket.status !== "closed" && (
                <form onSubmit={handleReply} className="flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 input-field text-sm py-2"
                  />
                  <button type="submit" disabled={replying || !reply.trim()} className="btn-primary px-4 py-2 text-sm">
                    {replying ? "..." : "Reply"}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div className="card p-10 text-center text-gray-400">
              <p>Select a ticket to view details and reply</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
